import useOnClickOutside from 'common/hooks/useOnClickOutside';
import React, { useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextField from 'components/Form/TextField';
import Button from 'components/Form/Button';
import { Link, useNavigate } from 'react-router-dom';
import {
  selectAccountDetails,
  selectIsAuthenticated
} from 'selectors/account-selector';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchUserInformation, login, logout } from 'thunks/account-thunk';
import { AccountTypeEnum } from 'apps/user/types/user.enum.type';

interface IProps {
  onDismiss: React.Dispatch<React.SetStateAction<boolean>>;
}

const LoginModal: React.FC<IProps> = ({ onDismiss }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { user, error: loginError } = useAppSelector(selectAccountDetails);

  const rootRef = useRef<HTMLDivElement>(null);
  const resetAndDismissDropdown = () => {
    onDismiss(false);
  };

  useOnClickOutside(rootRef, resetAndDismissDropdown);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validationSchema: Yup.object({
      password: Yup.string().required('Password is required').nullable(),
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required')
        .nullable()
    }),
    onSubmit: (values, { setFieldTouched }) => {
      const { email, password } = values;
      for (const key in values) {
        setFieldTouched(key, true);
      }
      try {
        dispatch(login({ email: email.trim(), password: password.trim() }));
      } catch (error) {
        console.log('Error', error);
      }
    }
  });

  useEffect(() => {
    if (loginError) {
      formik.setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, loginError, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      if (user) {
        dispatch(fetchUserInformation());
        if (user.accountType === AccountTypeEnum.ADMIN) {
          navigate('/admin');
          return;
        } else {
          navigate('/dashboard');
        }
      } else {
        dispatch(logout());
        navigate('/');
      }
    }
  }, [user, isAuthenticated, dispatch, navigate]);

  return (
    <div className="relatives z-50">
      <div
        className="absolute right-[15rem] top-[5rem] w-[22rem] rounded-md bg-white text-[#35353580] shadow-xl shadow-slate-400"
        ref={rootRef}
      >
        <div className="flex flex-col items-center justify-center p-5 text-center">
          <div className="">
            <p className="text-xl font-bold text-[#363636]">
              Login to your account
            </p>
            <p className="text-[8px] font-bold text-[#363636]">
              Pickup from where you left
            </p>
          </div>
          <form onSubmit={formik.handleSubmit} className="mt-9 w-full px-3">
            <div className="space-y-8">
              <TextField
                name="email"
                placeholder="Email or Phone Number"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label=""
                type="email"
                error={formik.touched.email ? formik.errors.email : ''}
                isRequired
                size="md"
                className="rounded-2xl border-[1.5px] border-gray-300 py-3"
              />
              <TextField
                name="password"
                placeholder="Password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label=""
                type="password"
                error={formik.touched.password ? formik.errors.password : ''}
                isRequired
                size="md"
                className="rounded-2xl border-[1.5px] border-gray-300 py-3"
              />
              <Button
                variant="primary"
                size="custom"
                className="mt-10 w-full rounded py-2 text-lg font-medium"
                label="Login"
                type="submit"
                loading={formik.isSubmitting}
                disabled={formik.isSubmitting || !formik.isValid}
              ></Button>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <span className="text-[8px] font-medium text-black">
                    Don&apos;t have an account?
                  </span>
                  <Link
                    className="text-[8px] font-bold text-black"
                    to="/get-started"
                  >
                    Register
                  </Link>
                </div>
                <Link className="text-[8px] font-medium text-black" to="">
                  Forgot Password?
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
